/**
 * Portal — the launchpad.
 *
 * Shows exactly the tools this user has been granted, and nothing else. The tile
 * list comes straight from /me, so it is a render of what the API already said
 * rather than a permission question asked again in the browser. A user with no
 * grants sees an explicit empty state, not a blank page: "nothing here" and
 * "something is broken" must not look the same.
 */
import { AppShell, PageHeader, useMe, useAuth } from "@cqt/platform";
import type { GrantedTool, Level } from "@cqt/platform";
import { ToolIcon } from "./ToolIcon";

const LEVEL_LABEL: Record<Level, string> = {
  none: "No access",
  view: "View only",
  edit: "Can edit",
  manage: "Manage",
};

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ width: 13, height: 13, flex: "none" }}>
      <path d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

function Tile({ tool }: { tool: GrantedTool }) {
  // External tools open in a new tab: they are a different origin (Teable is on
  // its own port, and in production has no public hostname at all), so replacing
  // the current tab would strand someone outside the platform with no way back.
  const external = tool.is_external;
  const contents = (
    <>
      <span className="tile__icon" aria-hidden="true">
        <ToolIcon name={tool.icon} />
      </span>
      <span className="tile__body">
        <span className="tile__name">
          {tool.name}
          {external ? (
            <span className="tile__ext" title="Opens in a new tab">
              <ExternalIcon />
            </span>
          ) : null}
        </span>
        {tool.description ? <span className="tile__desc">{tool.description}</span> : null}
        {tool.is_coming_soon ? <span className="tile__coming-soon">Coming soon</span> : null}
        {/* Teable has no public IP in production, so the tile has to say that a
            tunnel is needed — otherwise it just looks broken. */}
        {tool.requires_tunnel ? (
          <span className="tile__warn">Needs an IAP tunnel to be open</span>
        ) : null}
      </span>
      <span className={`chip ${tool.level === "view" ? "" : "chip--accent"}`}>
        {LEVEL_LABEL[tool.level]}
      </span>
    </>
  );

  // The upcoming modules are real permission targets, but no application exists
  // behind them yet. A non-link makes that state clear and prevents a dead route.
  if (tool.is_coming_soon) {
    return (
      <article className="tile tile--coming-soon" aria-label={`${tool.name} — coming soon`}>
        {contents}
      </article>
    );
  }

  return (
    <a
      className="tile"
      href={tool.path}
      {...(external
        ? { target: "_blank", rel: "noreferrer noopener", "data-external": true }
        : {})}
    >
      {contents}
    </a>
  );
}

function NoTools() {
  const { me } = useAuth();
  return (
    <div className="card">
      <div className="empty">
        <div className="empty__title">No tools yet</div>
        <p style={{ maxWidth: 460, margin: 0 }}>
          Your account is set up, but nobody has given you access to anything yet.
          An administrator needs to grant you a tool before it appears here.
        </p>
        <p className="meta" style={{ marginTop: 4 }}>
          Signed in as {me.user.email}
        </p>
      </div>
    </div>
  );
}

export function App() {
  const me = useMe();
  const { config } = useAuth();

  // /me already filters to active, entitled tools; sorting here only guarantees
  // a stable order if the API's ordering ever changes.
  const tools = [...me.tools].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <AppShell>
      <PageHeader
        eyebrow={config.companyName}
        title={`Welcome${me.user.full_name ? `, ${me.user.full_name.split(" ")[0]}` : ""}`}
      />

      {tools.length === 0 ? (
        <NoTools />
      ) : (
        <>
          <p className="muted" style={{ marginTop: -12, marginBottom: 20 }}>
            {tools.length === 1 ? "One tool is" : `${tools.length} tools are`} available to you.
          </p>
          <div className="tiles">
            {tools.map((tool) => (
              <Tile key={tool.key} tool={tool} />
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
